/**
 * number.ts
 *
 * Utility functions for handling numbers, such as rounding.
 *
 * We use the Decimal.js library for precise decimal arithmetic.
 * This is particularly useful for financial calculations to avoid
 * floating-point errors due to JavaScript's IEEE 754 double-precision
 * floating-point format.
 */

import Decimal from 'decimal.js';

/**
 * Rounds a number to a specified precision using Decimal.js then returns the
 * result as a string.
 *
 * @param {number} value - The number to round.
 * @param {number} precision - The number of decimal places to round to.
 * @returns {string} The rounded number.
 */
export const preciseRound = (value: number, precision = 0): string => {
  // Check for invalid input to prevent unexpected behavior
  if (typeof value !== 'number' || isNaN(value)) {
    console.error(`Non-number: ${value} provided to preciseRound`);
    return value.toString(); // Return value to avoid breaking the application
  }

  // Convert the number to a string BEFORE passing it to Decimal.js.
  // This is the most critical step to preserve the number's intended decimal precision
  // and avoid passing an already-corrupted floating-point value.
  const numAsString = value.toString();

  // Create a Decimal instance from the string representation of the number
  const decimalValue = new Decimal(numAsString);

  // Decimal.ROUND_HALF_UP is the standard "0.5 rounds up" behavior.
  const roundedDecimal = decimalValue.toDecimalPlaces(
    precision,
    Decimal.ROUND_HALF_UP,
  );

  return roundedDecimal.toString();
};
